/**
 * Pipeline step definitions shared with the frontend.
 *
 * Embodiment is a sub-step of Detailed Description per 37 CFR 1.77(b).
 * The web wizard imports these definitions to avoid
 * duplicating step IDs, labels, and tree structure.
 */

/** A node in the pipeline step tree. */
export interface PipelineStepNode {
  id: string;
  label: string;
  children?: PipelineStepNode[];
}

/**
 * Complete pipeline step tree — single source of truth for the frontend.
 *
 * A frontend may display a subset (e.g. a caller omits content_analysis
 * and claim_validation) or override labels for space-constrained UIs.
 */
export const PIPELINE_STEP_TREE: PipelineStepNode[] = [
  { id: "content_analysis", label: "Analyzing Content & Planning" },
  { id: "map_hierarchy", label: "Mapping Claim Hierarchy" },
  { id: "map_alternatives", label: "Mapping Alternative Elements" },
  { id: "extract_problems", label: "Extracting Problems Solved" },
  { id: "extract_structured_claims", label: "Extracting Structured Claims" },
  { id: "abstract", label: "Drafting Abstract" },
  { id: "brief_summary", label: "Writing Brief Summary" },
  { id: "background", label: "Composing Background" },
  {
    id: "detailed_description",
    label: "Writing Detailed Description",
    children: [{ id: "embodiment", label: "Drafting Embodiments" }],
  },
  { id: "claim_validation", label: "Validating Claim Support" },
];

/** Flat list of all step IDs (parents + children) in tree order. */
export const PIPELINE_STEP_IDS: string[] = PIPELINE_STEP_TREE.flatMap((s) => [
  s.id,
  ...(s.children?.map((c) => c.id) ?? []),
]);

/** Flat map of step ID → label for all steps (parents + children). */
export const PIPELINE_STEP_LABELS: Record<string, string> =
  Object.fromEntries(
    PIPELINE_STEP_TREE.flatMap((s) => [
      [s.id, s.label] as const,
      ...(s.children?.map((c) => [c.id, c.label] as const) ?? []),
    ]),
  );

/**
 * Short-form labels for space-constrained UIs.
 *
 * Only includes ids where the compact label differs from the canonical
 * label. Consumers fall back to `PIPELINE_STEP_LABELS[id]` for any id
 * not listed here.
 */
export const PIPELINE_STEP_SHORT_LABELS: Record<string, string> = {
  map_hierarchy: "Map Hierarchy",
  map_alternatives: "Map Alternatives",
  extract_problems: "Extract Problems",
  extract_structured_claims: "Extract Claims",
  abstract: "Abstract",
  brief_summary: "Brief Summary",
  background: "Background",
  detailed_description: "Detailed Description",
  embodiment: "Embodiments",
};

// ── DAG Stage Tree ───────────────────────────────────────────────────────
//
// Groups DD sub-stages under a parent for display. Frontends use this
// tree to render grouped execution progress; parent node status is
// derived from children at render time.

export const DAG_STAGE_TREE: PipelineStepNode[] = [
  {
    id: "ingestion_preprime",
    label: "Ingestion",
    children: [
      { id: "analyze_disclosure", label: "Analyzing Disclosure" },
      { id: "extract_claims", label: "Extracting Claims" },
      { id: "map_hierarchy", label: "Mapping Hierarchy" },
      { id: "map_alternatives", label: "Mapping Alternatives" },
      { id: "extract_problems", label: "Extracting Problems" },
      { id: "analyze_content", label: "Analyzing Prior Art" },
    ],
  },
  { id: "claims", label: "Claims" },
  { id: "review", label: "Claims Review" },
  {
    id: "detailed_description",
    label: "Detailed Description",
    children: [
      { id: "dd_skeleton", label: "Description Outline" },
      { id: "plan_content", label: "Content Planning" },
      { id: "verify_coverage", label: "Coverage Verification" },
      { id: "generate_support", label: "Support Generation" },
      { id: "write_dd_section", label: "Section Composition" },
      { id: "assemble_dd", label: "DD Assembly" },
    ],
  },
  {
    id: "embodiment",
    label: "Embodiments",
    children: [
      { id: "emb_skeleton", label: "Embodiment Outline" },
      { id: "plan_emb_content", label: "Embodiment Planning" },
      { id: "generate_emb_support", label: "Embodiment Support" },
      { id: "write_emb_sections", label: "Writing Embodiments" },
      { id: "assemble_embodiment", label: "Embodiment Assembly" },
    ],
  },
  { id: "background", label: "Background" },
  { id: "brief_summary", label: "Brief Summary" },
  { id: "abstract", label: "Abstract" },
  { id: "post_process", label: "Post-Processing" },
  { id: "compliance", label: "Compliance Check" },
  { id: "validation", label: "Validation" },
];

/** Flat map of DAG stage ID → label (parents + children). */
export const DAG_STAGE_LABELS: Record<string, string> = Object.fromEntries(
  DAG_STAGE_TREE.flatMap((s) => [
    [s.id, s.label] as const,
    ...(s.children?.map((c) => [c.id, c.label] as const) ?? []),
  ]),
);

// ── Section output labels ─────────────────────────────────────────────────

/**
 * Human-readable labels for pipeline output section keys.
 *
 * Covers both top-level sections (claims, abstract, etc.) and
 * intermediate sub-sections (dd_section_N, emb_section_N) that the
 * DAG executor writes to the sections dict.
 */
const _STATIC_SECTION_LABELS: Record<string, string> = {
  claims: "Claims",
  abstract: "Abstract",
  brief_summary: "Brief Summary",
  background: "Background",
  detailed_description: "Detailed Description",
  embodiment: "Embodiments",
  // DAG intermediate sections
  dd_skeleton: "Description Outline",
  plan_content: "Content Planning",
  verify_coverage: "Coverage Verification",
  generate_support: "Support Generation",
  write_dd_section: "Section Composition",
  assemble_dd: "DD Assembly",
  // Embodiment sub-skills
  emb_preferred: "Preferred Embodiment",
  emb_examples: "Working Examples",
  emb_use_claims: "Use Claims Embodiment",
};

/**
 * The patent document sections shown to the attorney in the completion card.
 * Intermediate sub-sections (dd_section_*, emb_section_*) are folded into
 * these assembled sections by the pipeline and should not be displayed.
 */
const _PATENT_DOCUMENT_SECTIONS = new Set([
  "claims",
  "abstract",
  "brief_summary",
  "background",
  "detailed_description",
]);

/**
 * Filter a sections dict to only the final patent document sections,
 * removing intermediate sub-sections that are folded into assembled outputs.
 *
 * Falls back to showing all sections if none of the known document
 * sections are present (e.g. partial pipeline output).
 */
export function filterDocumentSections(
  sections: Record<string, string>,
): Record<string, string> {
  const keys = Object.keys(sections);
  const documentKeys = keys.filter((k) => _PATENT_DOCUMENT_SECTIONS.has(k));

  // If we have at least one document-level section, show only those.
  // Otherwise show everything (partial pipeline output).
  if (documentKeys.length > 0) {
    const result: Record<string, string> = {};
    for (const k of documentKeys) {
      result[k] = sections[k];
    }
    return result;
  }
  return sections;
}

/**
 * Resolve a pipeline section key to a human-readable label.
 *
 * Handles static keys, numbered sub-sections (`dd_section_1` →
 * "Detailed Description §1", `emb_section_5` → "Embodiment §5"),
 * and falls back to capitalized underscore replacement.
 */
export function formatSectionLabel(key: string): string {
  const label = _STATIC_SECTION_LABELS[key];
  if (label) return label;

  // dd_section_N → "Detailed Description §N"
  const ddMatch = key.match(/^dd_section_(\d+)$/);
  if (ddMatch) return `Detailed Description §${ddMatch[1]}`;

  // emb_section_N → "Embodiment §N"
  const embMatch = key.match(/^emb_section_(\d+)$/);
  if (embMatch) return `Embodiment §${embMatch[1]}`;

  // Fallback: capitalize + underscore replacement
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
